import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import NoticeComponent from '../../components/NoticeComponent';
import SearchBar from '../../components/SearchBar';
import { PaymentDTO } from "../../types/types"; // Ensure correct types
import { instance } from "../../api/axiosInstance";
import Cookies from "js-cookie";
import PaymentItem from "./PaymentItem";
import YearFilterTabs from "./YearFilterTabs";

const years = ['최근 6개월', '2024'];

export default function PurchaseDetails() {
  const [selectedYear, setSelectedYear] = useState<string>('최근 6개월');
  const [payments, setPayments] = useState<PaymentDTO[]>([]);
  const [filteredPayments, setFilteredPayments] = useState<PaymentDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [noPayments, setNoPayments] = useState<boolean>(false); // New state for no payments
  const navigate = useNavigate();

  // Fetch payments from API
  const fetchPayments = async () => {
    const accessToken = Cookies.get('access_token');
    if (!accessToken) {
      setError('인증 토큰이 없습니다. 로그인해주세요.');
      setLoading(false);
      return;
    }

    try {
      const response = await instance.get<PaymentDTO[]>('/auth/payments/my', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.status === 204) {
        setNoPayments(true); // Set no payments if 204 status
      } else if (Array.isArray(response.data)) {
        setPayments(response.data);
        setNoPayments(response.data.length === 0); // Check if no payments
      } else {
        setError('결제 내역을 가져오는 중 오류가 발생했습니다.');
      }
    } catch (error) {
      setError('결제 내역을 가져오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // Filter payments based on selected year
  const filterPayments = () => {
    const currentDate = new Date();
    let filtered: PaymentDTO[] = [];

    if (selectedYear === '최근 6개월') {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(currentDate.getMonth() - 6);
      filtered = payments.filter(payment => {
        if (!payment.approvedAt) return false;
        const paymentDate = new Date(payment.approvedAt);
        return paymentDate >= sixMonthsAgo && paymentDate <= currentDate;
      });
    } else {
      filtered = payments.filter(payment => {
        if (!payment.approvedAt) return false;
        const paymentYear = new Date(payment.approvedAt).getFullYear().toString();
        return paymentYear === selectedYear;
      });
    }

    setFilteredPayments(filtered);
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  useEffect(() => {
    filterPayments();
  }, [selectedYear, payments]);

  // Group payments by date and order number
  const groupedPayments = useMemo(() => {
    const groups: { [key: string]: { payments: PaymentDTO[], date: string } } = {};

    filteredPayments.forEach((payment) => {
      const dateKey = payment.approvedAt
        ? new Date(payment.approvedAt).toLocaleDateString('ko-KR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
        : '날짜 정보 없음';

      const orderKey = payment.orderName; // Use the correct property name

      if (!groups[dateKey]) {
        groups[dateKey] = { payments: [], date: dateKey };
      }

      groups[dateKey].payments.push(payment);
    });

    return Object.keys(groups).map((date) => {
      const orderGroups: { [key: string]: PaymentDTO[] } = {};

      groups[date].payments.forEach((payment) => {
        const orderKey = payment.orderName; // Use the correct property name

        if (!orderGroups[orderKey]) {
          orderGroups[orderKey] = [];
        }
        orderGroups[orderKey].push(payment);
      });

      const orders = Object.keys(orderGroups).map((orderNumber) => ({
        orderNumber,
        payments: orderGroups[orderNumber],
      }));

      return { date, orders };
    });
  }, [filteredPayments]);

  // Loading state handling
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="loader"></div>
      </div>
    );
  }

  // Error state handling
  if (error) {
    return (
      <div className="flex flex-col items-center mt-20">
        <p className="text-red-500 mb-4">{error}</p>
        <button
          onClick={() => window.location.href = '/login'}
          className="bg-MainColor text-white px-4 py-2 rounded"
        >
          로그인 페이지로 이동
        </button>
      </div>
    );
  }

  return (
    <section className="max-w-[1200px] w-full mx-auto mt-[130px] mb-[60px]">
      <NoticeComponent title="구매내역" content="" />
      <div className="max-w-[450px]">
        <SearchBar />
      </div>

      {/* Year filter tabs */}
      <YearFilterTabs
        years={years}
        selectedYear={selectedYear}
        onSelectYear={setSelectedYear}
      />

      {/* Purchase list */}
      <article className='h-screen overflow-y-auto'>
        {noPayments ? ( // Check if no payments
          <p className="text-center mt-20">해당 기간의 구매 내역이 없습니다.</p>
        ) : groupedPayments.length > 0 ? (
          groupedPayments.map((group) => (
            <div className="mt-12" key={group.date}>
              <h3 className="text-lg font-bold mb-2">{group.date} 주문</h3>
              {group.orders.map((order) => {
                const firstPayment = order.payments[0]; // First payment in the order
                const productCount = order.payments.length; // Total number of products in this order
                const firstProductName = firstPayment.product?.name || '상품 정보 없음';

                return (
                  <div key={order.orderNumber} className="mb-4">
                    <h4 className="font-semibold">
                      {productCount > 1
                        ? `${firstProductName} 외 ${productCount - 1}개`
                        : firstProductName
                      }
                    </h4>
                    {order.payments.map((payment: PaymentDTO) => {
                      const formattedDate = payment.approvedAt
                        ? new Date(payment.approvedAt).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }) + ' 주문'
                        : '날짜 정보 없음';

                      return (
                        <PaymentItem
                          key={payment.id}
                          payment={payment}
                          formattedDate={formattedDate}
                        />
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))
        ) : (
          <p className="text-center mt-20">해당 기간의 구매 내역이 없습니다.</p>
        )}
      </article>
    </section>
  );
}
